import React, { useState, useEffect, useCallback } from "react";
import Layout from "../../components/Layout/Layout";
import { SearchIcon, QuestionMarkCircleIcon } from "@heroicons/react/solid";
import { DocumentDownloadIcon } from "@heroicons/react/outline";
import ScheduledServices from "../../services/ScheduledServices";
import { formatDate, convert } from "./../../utils/convertDate";
import DatePicker from "react-date-picker";
import { ScheduledTransferTable } from "./ScheduledTransferTable";
import Paginate from "../../utils/Paginate";
import ViewTransferModal from "./viewTransferModal";

const statusStyles = {
  Active: "bg-green-500 text-white",
  Expired: "bg-orange-600 text-white",
  Pending: "bg-orange-600 text-white",
  Disabled: "bg-amber-400 text-white",
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function ScheduledTransfers() {
  const [transfers, setTransfer] = useState([]);
  const [search, setSearch] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [prevPage, setPrev] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalPage, setPage] = useState(0);
  const [nextPage, setNextPage] = useState(false);
  const [status, setStatus] = useState("");
  const [active, setActive] = useState("All");
  const [pageLength, setPageLength] = useState(0);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("");

  // Modal states
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [transferDetails, setTransferDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Date states
  const oldFrom = new Date().setDate(new Date().getDate() - 7);
  const [formValues, setFormValues] = useState({
    from: new Date(oldFrom),
    to: new Date(),
  });
  const defaultTo = formatDate(new Date());
  const longNew = formatDate(defaultTo);
  const [too, setToo] = useState(convert(longNew));
  const defaultFrom = formatDate(new Date().setDate(new Date().getDate() - 7));
  const longDate = formatDate(defaultFrom);
  const [fromm, setFromm] = useState(convert(longDate));
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  // Fetch transfers function
  const listScheduled = useCallback(
    async (page = pageIndex) => {
      setLoading(true);
      try {
        const list = await ScheduledServices.listSchedules(
          page,
          10,
          search,
          fromm,
          too,
          status,
          type
        );
        if (list.data) {
          setTransfer([...list.data.result]);
          setPageLength(list.data.result?.length || 0);
          setPageNumber(list.data.totalCount || 0);
          setPageIndex(list.data.pageIndex || 1);
          setPrev(list.data.hasPreviousPage || false);
          setPage(list.data.totalPages || 0);
          setNextPage(list.data.hasNextPage || false);
        }
      } catch (error) {
        console.error("Error fetching transfers:", error);
      } finally {
        setLoading(false);
      }
    },
    [pageIndex, search, fromm, too, status, type]
  );

  // Apply date range filter
  const range = async (e) => {
    e.preventDefault();
    const newDate = formatDate(formValues.from);
    setFrom(formValues.from);
    var longDate = formatDate(newDate);
    setFromm(convert(longDate));

    const toDate = formatDate(formValues.to);
    setTo(formValues.to);
    setToo(convert(toDate));

    setPageIndex(1);
    await listScheduled(1);
  };

  // Handle transfer type filter
  const setTranType = async (e) => {
    const selectedValue = e.target.value;
    setType(selectedValue === "All" ? "" : selectedValue);
    setPageIndex(1);
    await listScheduled(1);
  };

  // Handle tab changes
  const setTab = async (tab) => {
    const statusValue = tab === "All" ? "" : tab;
    setActive(tab);
    setStatus(statusValue);
    setPageIndex(1);
    await listScheduled(1);
  };

  // Export CSV
  const exportCSV = async () => {
    try {
      const resp = await ScheduledServices.exportSchedule(
        search,
        fromm,
        too,
        status,
        type
      );
      if (resp.data) {
        window.open(resp.data);
      }
    } catch (error) {
      console.error("Error exporting CSV:", error);
    }
  };

  // Handle search
  const searchTransfer = (text) => {
    setSearch(text);
  };

  // Handle search submit
  const handleSearchSubmit = async (e) => {
    if (e) e.preventDefault();
    setPageIndex(1);
    await listScheduled(1);
  };

  // View transfer details
  const handleViewDetails = async (transfer) => {
    setSelectedTransfer(transfer);
    setLoadingDetails(true);
    setIsModalOpen(true);

    try {
      const response = await ScheduledServices.viewSchedule(transfer.id);
      if (response.data) {
        setTransferDetails(response.data);
      }
    } catch (error) {
      console.error("Error fetching transfer details:", error);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Date change handlers
  const dateValidation = {
    fromHandler: (event) => {
      setFormValues({ ...formValues, from: event });
    },
    toHandler: (event) => {
      setFormValues({ ...formValues, to: event });
    },
  };

  // Initial load
  useEffect(() => {
    listScheduled();
  }, []);

  // Refresh when pageIndex changes
  useEffect(() => {
    if (pageIndex > 0) {
      listScheduled();
    }
  }, [pageIndex, listScheduled]);

  return (
    <>
      <Layout
        title={"Scheduled Transfers"}
        pageTag={"transfers"}
        current={"/scheduled/transfers"}
      >
        <React.Fragment>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Key Indicators
              </h3>
              <div className="overflow-x-auto">
                <nav
                  className="relative z-0 rounded-lg shadow flex divide-x divide-gray-200"
                  aria-label="Tabs"
                >
                  <ul
                    className="relative z-0 rounded-lg shadow flex divide-x divide-gray-200"
                    id="myTab"
                    aria-label="Tabs"
                  >
                    <li className="nav-item">
                      <button
                        className={`group relative border text-gray-900 min-w-max p-3 flex-1 overflow-hidden bg-white px-4 text-sm font-medium text-center hover:bg-gray-50 focus:z-10 ${
                          active === "All" ? "bg-gray-50 text-indigo-600" : ""
                        }`}
                        onClick={() => setTab("All")}
                      >
                        All Transfers
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`group relative border min-w-max p-3 flex-1 overflow-hidden bg-white px-4 text-sm font-medium text-center hover:bg-gray-50 focus:z-10 ${
                          active === "Active"
                            ? "bg-gray-50 text-indigo-600"
                            : ""
                        }`}
                        onClick={() => setTab("Active")}
                      >
                        Active Transfers
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`group relative border min-w-max p-3 flex-1 overflow-hidden bg-white px-4 text-sm font-medium text-center hover:bg-gray-50 focus:z-10 ${
                          active === "Disabled"
                            ? "bg-gray-50 text-indigo-600"
                            : ""
                        }`}
                        onClick={() => setTab("Disabled")}
                      >
                        Disabled Transfers
                      </button>
                    </li>
                    <li className="nav-item">
                      <button
                        className={`group relative border min-w-max p-3 flex-1 overflow-hidden bg-white px-4 text-sm font-medium text-center hover:bg-gray-50 focus:z-10 ${
                          active === "Expired"
                            ? "bg-gray-50 text-indigo-600"
                            : ""
                        }`}
                        onClick={() => setTab("Expired")}
                      >
                        Expired Transfers
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>

              <div className="mt-4 sm:mt-0  sm:flex-none">
                <button
                  type="button"
                  onClick={exportCSV}
                  className="hover:text-white active:text-white inline-flex mr-2 items-center justify-center rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                >
                  <DocumentDownloadIcon className="w-4 mr-1.5" />
                  Export CSV
                </button>
              </div>
            </div>

            <div className="sm:flex sm:items-center mt-10">
              <form onSubmit={handleSearchSubmit} className="w-full">
                <div className="sm:flex-auto flex flex-col lg:flex-row gap-4 items-end">
                  <div className="flex flex-col lg:flex-row gap-4">
                    <div>
                      <p className="text-sm leading-3 text-gray-500">From</p>
                      <div>
                        <DatePicker
                          onChange={dateValidation.fromHandler}
                          value={formValues.from}
                        />
                      </div>
                    </div>
                    <div>
                      <p className="text-sm leading-3 text-gray-500">To</p>
                      <div>
                        <DatePicker
                          onChange={dateValidation.toHandler}
                          value={formValues.to}
                        />
                      </div>
                    </div>
                    <div className="mt-5">
                      <button
                        type="button"
                        onClick={range}
                        className="hover:text-white active:text-white inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        Apply Dates
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-1 gap-2">
                    <div className="mt-5 relative rounded-md shadow-sm flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <SearchIcon
                          className="h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                      </div>
                      <input
                        type="text"
                        name="user"
                        id="user"
                        onChange={(e) => {
                          searchTransfer(e.target.value);
                        }}
                        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                        placeholder="Search transfers"
                      />
                      <div className=" absolute inset-y-0 right-0 flex items-center pr-3">
                        <div className="tooltip">
                          <QuestionMarkCircleIcon className="w-4 mr-1.5 text-gray-400" />
                          <span className="tooltiptext">
                            {" "}
                            <p>
                              Search by source account number,destination
                              account number and destination account name
                            </p>
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-5">
                      <button
                        type="submit"
                        className="hover:text-white active:text-white inline-flex mr-2 items-center justify-center rounded-md border border-transparent bg-indigo-600 px-6 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        Search
                      </button>
                    </div>

                    <div className="mt-5 relative rounded-md shadow-sm">
                      <select
                        id="currency"
                        name="currency"
                        value={type || "All"}
                        onChange={(e) => setTranType(e)}
                        className="focus:ring-indigo-500 focus:border-indigo-500 h-full py-2 pl-2 pr-7 border-gray-300 bg-transparent text-gray-500 sm:text-sm rounded-md"
                      >
                        <option value={"All"}>All Types</option>
                        <option value={"Intra"}>Intra</option>
                        <option value="Inter">Inter</option>
                      </select>
                    </div>
                  </div>
                </div>
              </form>
            </div>

            {loading ? (
              <div className="mt-8 flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              </div>
            ) : transfers.length === 0 ? (
              <div className="mt-8 text-center py-12 bg-gray-50 rounded-lg">
                <p className="text-gray-500">No scheduled transfers found</p>
              </div>
            ) : (
              <>
                <ScheduledTransferTable
                  transfers={transfers}
                  onViewDetails={handleViewDetails}
                />

                <Paginate
                  nextPage={nextPage}
                  pageIndex={pageIndex}
                  totalCount={pageNumber}
                  prevPage={prevPage}
                  totalPages={totalPage}
                  setPageIndex={setPageIndex}
                />
              </>
            )}
          </div>
        </React.Fragment>
      </Layout>

      <ViewTransferModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        transfer={selectedTransfer}
        transferDetails={transferDetails}
        loading={loadingDetails}
      />
    </>
  );
}
